import { Command, Lock, Plus, RefreshCw, SearchX, Unlink } from "lucide-react";
import { useMemo, useState } from "react";

import { PolicyEditorDrawer } from "@/modules/dashboard/settings/components/access/PolicyEditorDrawer";
import type { AccessPolicy } from "@/modules/dashboard/settings/types/access.types";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import { NoAccessView } from "@/shared/components/NoAccessView";
import { useTranslations } from "@/shared/lang/useTranslations";
import { toastApiError } from "@/shared/utils/toast";

import { AccessCatalogToolbar } from "../components/AccessCatalogToolbar";
import { PoliciesCards } from "../components/PoliciesCards";
import { PoliciesKpiStrip } from "../components/PoliciesKpiStrip";
import { PoliciesTable } from "../components/PoliciesTable";
import { usePoliciesCatalog } from "../hooks/usePoliciesCatalog";

/**
 * `/app/access/policies` — the policies catalog for Coloca staff.
 *
 * A policy is a set of `resource:action` statements under one effect. It never reaches a principal directly:
 * the only link the backend has is policy → role → principal, which is why this view sits beside Roles rather
 * than beside Users.
 *
 * The editor is `PolicyEditorDrawer`, imported from Settings unchanged — the same drawer an organization uses
 * for the same catalog, including its permission grid and its defensive handling of the 409 a delete gets
 * while a role still links the policy.
 */
const PoliciesPage = () => {
  const { t } = useTranslations("settings");
  const { t: tGlobal } = useTranslations("global");

  const catalog = usePoliciesCatalog();
  const [editor, setEditor] = useState<{ policy: AccessPolicy | null } | null>(null);

  useAppShellBreadcrumb(
    useMemo(
      () => [
        { label: tGlobal("sidebar_section_gestion") },
        { label: tGlobal("sidebar_menu_policies"), to: "/app/access/policies" },
      ],
      [tGlobal],
    ),
  );

  const editorPolicy = editor?.policy ?? null;
  // Creating is never read-only; editing is, unless the caller may write AND the policy itself is writable.
  const editorReadOnly = editorPolicy ? !catalog.canUpdate || !catalog.isEditable(editorPolicy) : false;

  const reload = () => {
    catalog.reload().catch(toastApiError);
  };

  if (catalog.hasNoAccess) return <NoAccessView />;

  const empty = catalog.hasActiveFilters ? (
    <ColocaEmpty
      icon={SearchX}
      title={t("access_policies_empty_filtered_title")}
      description={t("access_page_empty_filtered_body")}
      surface="card"
    >
      <ColocaButton variant="secondary" size="sm" onClick={catalog.clearFilters}>
        {t("access_page_clear_filters")}
      </ColocaButton>
    </ColocaEmpty>
  ) : (
    <ColocaEmpty
      icon={Command}
      title={t("access_policies_empty_title")}
      description={t("access_policies_empty_body")}
      surface="card"
    >
      {catalog.canCreate && (
        <ColocaButton variant="primary" size="sm" icon={Plus} onClick={() => setEditor({ policy: null })}>
          {t("access_policies_create")}
        </ColocaButton>
      )}
    </ColocaEmpty>
  );

  const footer =
    catalog.total > catalog.pageSize ? (
      <ColocaPagination
        page={catalog.page}
        perPage={catalog.pageSize}
        total={catalog.total}
        onPageChange={catalog.setPage}
        // The table's `footer` slot already draws the rule above the pagination.
        withSeparator={false}
      />
    ) : undefined;

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
      <ColocaPageHeader
        title={t("access_policies_title")}
        description={t("access_policies_sub")}
        actions={
          <>
            <ColocaButton
              variant="secondary"
              size="sm"
              icon={RefreshCw}
              onClick={reload}
              disabled={catalog.isLoading}
            >
              {t("access_page_refresh")}
            </ColocaButton>
            {catalog.canCreate && (
              <ColocaButton
                variant="primary"
                size="sm"
                icon={Plus}
                onClick={() => setEditor({ policy: null })}
              >
                {t("access_policies_create")}
              </ColocaButton>
            )}
          </>
        }
      />

      {/* `null` while loading: four zeros over a skeleton table would state that the catalog is empty. */}
      <PoliciesKpiStrip
        counts={catalog.isLoading ? null : catalog.counts}
        usageUnknown={catalog.usageUnknown}
        value={catalog.bucket}
        onChange={catalog.setBucket}
      />

      {/* Stated once, at the top, rather than as a tooltip on a disabled button nobody hovers: without the
          roles catalog we cannot know which policies a role links, and deleting one that IS linked answers
          409. So the delete affordance is withheld, and this says why. */}
      {catalog.usageUnknown && (
        <ColocaAlert variant="warning" icon={Unlink} text={t("access_policies_usage_unavailable")} />
      )}

      {!catalog.canCreate && (
        <ColocaAlert variant="secondary" icon={Lock} text={t("access_policies_readonly_note")} />
      )}

      <AccessCatalogToolbar
        query={catalog.query}
        onQueryChange={catalog.setQuery}
        searchPlaceholder={t("access_policies_search_placeholder")}
        scope={catalog.scope}
        onScopeChange={catalog.setScope}
      />

      <ColocaSurface padding="none" className="overflow-hidden">
        <div className="hidden lg:block">
          <PoliciesTable
            rows={catalog.rows}
            loading={catalog.isLoading}
            empty={empty}
            footer={footer}
            usageUnknown={catalog.usageUnknown}
            onOpen={(row) => setEditor({ policy: row.policy })}
          />
        </div>
        <div className="lg:hidden">
          {catalog.rows.length === 0 && !catalog.isLoading ? (
            <div className="p-6">{empty}</div>
          ) : (
            <PoliciesCards
              rows={catalog.rows}
              loading={catalog.isLoading}
              usageUnknown={catalog.usageUnknown}
              onOpen={(row) => setEditor({ policy: row.policy })}
            />
          )}
          {footer && <div className="border-t border-border px-4 py-3">{footer}</div>}
        </div>
      </ColocaSurface>

      <PolicyEditorDrawer
        open={!!editor}
        onOpenChange={(open) => {
          if (!open) setEditor(null);
        }}
        policy={editorPolicy}
        readOnly={editorReadOnly}
        usedByCount={editorPolicy ? catalog.usedByCount(editorPolicy.id) : 0}
        // Fails closed on `usageUnknown`: with the roles catalog unreadable, `usedByCount` would be 0 for
        // every policy and the drawer would offer a delete the backend answers with 409. Withholding the
        // affordance is the honest form of "we cannot tell whether this is safe".
        canDelete={
          editorPolicy
            ? catalog.canDelete && catalog.isEditable(editorPolicy) && !catalog.usageUnknown
            : false
        }
        onSaved={() => {
          setEditor(null);
          reload();
        }}
        onDeleted={() => {
          setEditor(null);
          reload();
        }}
      />
    </div>
  );
};

export default PoliciesPage;
